import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { api } from '../services/api';

/**
 * Game copy review (Odin's ruling 2026-10-31, "Nothing unvetted in a live
 * game"). Every wired pool (game pages and the /links closer), grouped by the
 * page it appears on. A line
 * serves in the game only once Odin approves it; unreviewed and tossed lines
 * never serve, and a pool with none approved leaves its slot empty. Editing an
 * approved line sends it back to unreviewed. A line added here is saved
 * approved. Tossed lines are kept and can be restored.
 */

export interface ReviewCategory {
  category: string;
  group: string;
  label: string;
  slot: string;
  review: string | null;
  word_note?: string | null;
}

type ReviewStatus = 'unreviewed' | 'approved' | 'tossed';

interface ReviewLine {
  id: number;
  category: string;
  line: string;
  active: number;
  sort_order: number;
  review_status: ReviewStatus;
  reviewed_at: string | null;
}

interface LineWrite {
  row: ReviewLine;
  warnings?: string[];
}

const PAGES = ['Result page', 'Chance pages', 'Share', 'Links page'];

const STATUS_LABEL: Record<ReviewStatus, string> = {
  unreviewed: 'Unreviewed',
  approved: 'Approved',
  tossed: 'Tossed',
};

export default function CopyReview({ categories }: { categories: ReviewCategory[] }) {
  const [lines, setLines] = useState<ReviewLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showTossed, setShowTossed] = useState(false);
  const [editing, setEditing] = useState<Record<number, string>>({});
  const [newLines, setNewLines] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/copy-pools/review') as { lines: ReviewLine[] };
      setLines(data.lines);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pools = useMemo(() => categories.filter((c) => c.review), [categories]);

  const byCategory = useMemo(() => {
    const out = new Map<string, ReviewLine[]>();
    for (const l of lines) {
      const list = out.get(l.category);
      if (list) list.push(l);
      else out.set(l.category, [l]);
    }
    return out;
  }, [lines]);

  const totals = useMemo(() => {
    const t = { unreviewed: 0, approved: 0, tossed: 0 };
    for (const l of lines) t[l.review_status] += 1;
    return t;
  }, [lines]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const replaceRow = (row: ReviewLine) => setLines((ls) => ls.map((l) => (l.id === row.id ? row : l)));

  const showWarnings = (warnings?: string[]) => {
    if (warnings && warnings.length) setNotice(`Saved, with linter warnings: ${warnings.join(' ')}`);
  };

  const review = (line: ReviewLine, action: 'approve' | 'toss' | 'restore') => run(async () => {
    const res = await api.put(`/api/copy-pools/lines/${line.id}/review`, { action }) as LineWrite;
    replaceRow(res.row);
  });

  const stopEditing = (id: number) => setEditing((e) => {
    const next = { ...e };
    delete next[id];
    return next;
  });

  const saveEdit = (line: ReviewLine) => {
    const text = editing[line.id];
    if (text === undefined) return;
    run(async () => {
      const res = await api.put(`/api/copy-pools/lines/${line.id}`, { line: text }) as LineWrite;
      replaceRow(res.row);
      stopEditing(line.id);
      showWarnings(res.warnings);
    });
  };

  const addLine = (category: string) => {
    const text = newLines[category];
    if (!text || !text.trim()) return;
    run(async () => {
      const res = await api.post(`/api/copy-pools/${category}`, { line: text }) as LineWrite;
      setLines((ls) => [...ls, res.row]);
      setNewLines((n) => ({ ...n, [category]: '' }));
      showWarnings(res.warnings);
    });
  };

  const onCmdEnter = (e: KeyboardEvent<HTMLTextAreaElement>, submit: () => void) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="copyreview">
      <div className="copyreview-bar">
        <span className="copyreview-totals">
          {totals.unreviewed} unreviewed · {totals.approved} approved · {totals.tossed} tossed
        </span>
        <label className="copypools-toggle">
          <input type="checkbox" checked={showTossed} onChange={(e) => setShowTossed(e.target.checked)} />
          Show tossed
        </label>
      </div>

      {error && (
        <div className="copypools-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {notice && <div className="copypools-warn">{notice}</div>}
      {loading && <p className="copypools-muted">Loading lines...</p>}

      {!loading && PAGES.map((page) => (
        <section key={page} className="copyreview-page">
          <h2 className="copyreview-page-title">{page}</h2>
          {pools.filter((p) => p.review === page).map((pool) => {
            const all = byCategory.get(pool.category) || [];
            const count = (s: ReviewStatus) => all.filter((l) => l.review_status === s).length;
            const served = all.filter((l) => l.review_status === 'approved' && l.active === 1).length;
            const shown = showTossed ? all : all.filter((l) => l.review_status !== 'tossed');
            return (
              <div key={pool.category} className="copyreview-pool">
                <div className="copyreview-pool-head">
                  <div>
                    <h3 className="copypools-cat-title">{pool.group}: {pool.label}</h3>
                    <p className="copypools-cat-meta"><code>{pool.category}</code> · {pool.slot}</p>
                    {pool.word_note && <p className="copypools-cat-meta">{pool.word_note}</p>}
                  </div>
                  <span className="copyreview-counts">
                    <span className="u">{count('unreviewed')} unreviewed</span>
                    <span className="a">{count('approved')} approved</span>
                    <span className="t">{count('tossed')} tossed</span>
                  </span>
                </div>

                {served === 0 && (
                  <div className="copypools-warn">No approved lines. This slot renders empty in the game.</div>
                )}

                <ol className="copypools-list">
                  {shown.map((l) => {
                    const draft = editing[l.id];
                    const isEditing = draft !== undefined;
                    return (
                      <li key={l.id} className={`copypools-line copyreview-line ${l.review_status}`}>
                        <span className={`copyreview-status ${l.review_status}`}>{STATUS_LABEL[l.review_status]}</span>
                        {isEditing ? (
                          <textarea
                            className="copypools-text"
                            value={draft}
                            rows={Math.max(1, Math.ceil(draft.length / 90))}
                            autoFocus
                            onChange={(e) => setEditing((d) => ({ ...d, [l.id]: e.target.value }))}
                            onKeyDown={(e) => onCmdEnter(e, () => saveEdit(l))}
                          />
                        ) : (
                          <p className="copyreview-text">
                            {l.line}
                            {l.active === 0 && l.review_status !== 'approved' && (
                              <span className="copyreview-off" title="Switched off in the old editor; Approve turns it on">was off</span>
                            )}
                          </p>
                        )}
                        <div className="copypools-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="primary"
                                disabled={busy || draft === l.line}
                                title={l.review_status === 'approved' ? 'Saving sends this line back to unreviewed' : undefined}
                                onClick={() => saveEdit(l)}
                              >
                                Save
                              </button>
                              <button disabled={busy} onClick={() => stopEditing(l.id)}>Cancel</button>
                            </>
                          ) : l.review_status === 'tossed' ? (
                            <button disabled={busy} onClick={() => review(l, 'restore')}>Restore</button>
                          ) : (
                            <>
                              {l.review_status === 'unreviewed' && (
                                <button className="primary" disabled={busy} onClick={() => review(l, 'approve')}>Approve</button>
                              )}
                              <button className="danger" disabled={busy} onClick={() => review(l, 'toss')}>Toss</button>
                              <button disabled={busy} onClick={() => setEditing((d) => ({ ...d, [l.id]: l.line }))}>Edit</button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
                {shown.length === 0 && <p className="copypools-muted">No lines{showTossed ? '' : ' to review'}.</p>}

                <div className="copypools-add copyreview-add">
                  <textarea
                    className="copypools-text"
                    placeholder="Add a line. Saved approved. ASCII only, no em dashes or semicolons. Cmd+Enter to add."
                    value={newLines[pool.category] || ''}
                    rows={1}
                    onChange={(e) => setNewLines((n) => ({ ...n, [pool.category]: e.target.value }))}
                    onKeyDown={(e) => onCmdEnter(e, () => addLine(pool.category))}
                  />
                  <button
                    className="primary"
                    disabled={busy || !(newLines[pool.category] || '').trim()}
                    onClick={() => addLine(pool.category)}
                  >
                    Add line
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
